<script setup>
import { computed } from 'vue'
import { useAgGridStore } from '@/stores/AgGrid'

/**
 * Define the component's props.
 *
 * @type {Props}
 */
defineProps({
    /**
     * Determine if the grid has changes.
     *
     * @type {Boolean}
     */
    hasChanges: {
        type: Boolean,
        default: false,
    },

    /**
     * Determine if the grid is loading.
     *
     * @type {Boolean}
     */
    loading: {
        type: Boolean,
        default: false,
    },
})

/**
 * Declare the component's store instance.
 *
 * @type {Pinia}
 */
const store = useAgGridStore()

/**
 * Register the component's emits.
 *
 * @type {Array}
 */
const emits = defineEmits([
    'autoSizeAll',
    'clearFilters',
    'exportToCsv',
    'exportToExcel',
    'openToolPanel',
    'refresh',
    'save',
    'search',
    'sizeToFit',
    'refresh',
    'removeColumnFilter',
    'resetColumns',
    'resetDefaultFilters',
])

/**
 * Get the filters from the store.
 *
 * @type {Object}
 */
const filters = computed(() => {
    return store.filters
})

/**
 * Handle the auto-size all event.
 *
 * @return {void}
 */
const onAutoSizeAll = () => {
    emits('autoSizeAll')
}

/**
 * Handle the clear filters event.
 *
 * @return {void}
 */
const onClearFilters = () => {
    emits('clearFilters')
}

/**
 * Handle the clear filters event.
 *
 * @return {void}
 */
const onResetDefaultFilters = () => {
    emits('resetDefaultFilters')
}

/**
 * Handle the export to CSV event.
 *
 * @return {void}
 */
const onExportToCsv = () => {
    emits('exportToCsv')
}

/**
 * Handle the export to Excel event.
 *
 * @return {void}
 */
const onExportToExcel = () => {
    emits('exportToExcel')
}

/**
 * Handle the open tool panel event.
 *
 * @param {String} panel
 * @return {void}
 */
const onOpenToolPanel = (panel) => {
    emits('openToolPanel', panel)
}

/**
 * Handle the remove column filter event.
 *
 * @param  {String} key
 * @return {void}
 */
const onRemoveColumnFilter = (key) => {
    emits('removeColumnFilter', key)
}

/**
 * Handle the refresh event.
 *
 * @return {void}
 */
const onRefresh = () => {
    emits('refresh')
}

/**
 * Handle the save event.
 *
 * @return {void}
 */
const onSave = () => {
    emits('save')
}

/**
 * Handle the search event.
 *
 * @param  {Event} event
 * @return {void}
 */
const onSearch = (event) => {
    emits('search', event.target.value)
}

/**
 * Handle the size to fit event.
 *
 * @return {void}
 */
const onSizeToFit = () => {
    emits('sizeToFit')
}

/**
 * Refresh the grid's columns to their default state.
 *
 * @return {Boolean}
 */
const resetColumns = () => {
    emits('resetColumns')
}
</script>

<template>
    <div class="p-2 flex flex-col">
        <div class="flex justify-between">
            <v-text-field
                clearable
                label="Search"
                density="compact"
                variant="outlined"
                append-inner-icon="fal fa-magnifying-glass"
                single-line
                hide-details
                style="max-width: 300px"
                class="mr-2 min-w-max"
                id="dataGrid-filter-text-box"
                name="search-input-field"
                @input="onSearch"
                @click:clear="onSearch"
            ></v-text-field>
            <div class="flex items-center">
                <v-btn
                    :disabled="!hasChanges"
                    id="save-dropdown-activator"
                    variant="tonal"
                    type="button"
                    class="mr-1"
                    prepend-icon="far fa-save"
                >
                    <template v-slot:prepend>
                        <v-icon size="small"></v-icon>
                    </template>
                    Save
                </v-btn>
                <v-menu
                    activator="#save-dropdown-activator"
                    :close-on-content-click="true"
                    location="bottom"
                >
                    <v-card max-width="400">
                        <div class="p-4">
                            This is a Bolt-defined view. Changes you make to
                            column settings (column visibility, order, and
                            sorting) can be saved locally by clicking 'Save
                            Column Settings' below. Those changes will not be
                            available to other users and will not persist if you
                            clear your cookies. Changes you make to filters are
                            only available temporarily. When you navigate to a
                            new tab or new page and then return to this view,
                            the default filter settings will be restored.
                        </div>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn variant="text"> Cancel </v-btn>
                            <v-btn
                                color="primary"
                                variant="flat"
                                @click="onSave"
                            >
                                Save
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>
                <v-btn
                    class="mr-1"
                    type="button"
                    variant="tonal"
                    prepend-icon="far fa-arrows-rotate"
                    :disabled="loading"
                    @click="onRefresh"
                >
                    <template v-slot:prepend>
                        <v-icon size="small"></v-icon>
                    </template>
                    Refresh
                </v-btn>
                <v-btn
                    variant="tonal"
                    class="mr-1"
                    @click.stop="onOpenToolPanel('filters')"
                    prepend-icon="far fa-bars-filter"
                    aria-controls="sideBarDrawer"
                >
                    <template v-slot:prepend>
                        <v-icon size="small"></v-icon>
                    </template>
                    Filters
                </v-btn>
                <v-btn
                    variant="tonal"
                    class="mr-1"
                    @click.stop="onOpenToolPanel('columns')"
                    prepend-icon="far fa-columns-3"
                    aria-controls="sideBarDrawer"
                >
                    <template v-slot:prepend>
                        <v-icon size="small"></v-icon>
                    </template>
                    Columns
                </v-btn>
                <v-btn
                    color="primary"
                    variant="tonal"
                    id="actions-menu-activator"
                    append-icon="fal fa-chevron-down"
                >
                    Actions
                    <template v-slot:append>
                        <v-icon size="small"></v-icon>
                    </template>
                </v-btn>
                <v-menu activator="#actions-menu-activator">
                    <v-list class="p-2">
                        <v-list-item
                            value=""
                            rounded="true"
                            @click="onExportToExcel"
                        >
                            <v-list-item-title>
                                Export Excel
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            value=""
                            rounded="true"
                            @click="onExportToCsv"
                        >
                            <v-list-item-title> Export CSV </v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            value=""
                            rounded="true"
                            @click="onAutoSizeAll"
                        >
                            <v-list-item-title> Auto-size </v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            value=""
                            rounded="true"
                            @click="onSizeToFit"
                        >
                            <v-list-item-title> Size to fit </v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            value=""
                            rounded="true"
                            @click="onClearFilters"
                        >
                            <v-list-item-title>
                                Clear filters
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            value=""
                            rounded="true"
                            @click="resetColumns"
                        >
                            <v-list-item-title>
                                Reset column defaults
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>
        </div>
        <div class="flex justify-between items-center pt-2">
            <div class="flex items-center">
                <v-chip-group
                    selected-class="text-deep-purple-accent-4"
                    mandatory
                    class="flex items-center"
                >
                    <v-chip
                        v-if="filters.length"
                        @click="onClearFilters"
                        variant="text"
                        color="primary"
                        class="mr-3"
                    >
                        Clear Filters
                    </v-chip>

                    <v-chip
                        v-else-if="!loading"
                        @click="onResetDefaultFilters"
                        variant="text"
                        color="primary"
                        class="mr-3"
                    >
                        Reset Filters
                    </v-chip>

                    <v-chip
                        v-for="filter in filters"
                        :key="filter.key"
                        @click="onRemoveColumnFilter(filter.key)"
                        color="primary"
                        append-icon="fat fa-xmark"
                    >
                        {{ filter.label }}
                        <template v-slot:append>
                            <v-icon size="small">close</v-icon>
                        </template>
                    </v-chip>
                </v-chip-group>
            </div>
        </div>
    </div>
</template>
